import React, {useMemo} from "react";
import {Box, Divider, Paper, Typography} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {selectMainScreenWallets} from "../../store/walletSlice";
import {selectCurrencies} from "../../store/currencySlice";
import WalletIcon from "./WalletIcon";
import WalletBalance from "./WalletBalance";
import NumberService from "../../services/NumberService";

export const MAIN_SCREEN_WALLETS_ANCHOR_ID = "main-screen-wallets";

// Sum wallet balances as JS Numbers — values are short decimal strings and
// totals never reach the precision boundary in practice.
const walletValue = (wallet) => {
    const raw = wallet?.value ?? wallet?.balance ?? 0;
    const n = typeof raw === "number" ? raw : parseFloat(raw);
    return Number.isFinite(n) ? n : 0;
};

const formatSigned = (value) => {
    const sign = value < 0 ? "-" : "+";
    return sign + NumberService.formatSum(Math.abs(value).toString());
};

const MainScreenWalletsCard = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const wallets = useSelector(selectMainScreenWallets);
    const currencies = useSelector(selectCurrencies) || [];

    const totals = useMemo(() => {
        const byCurrency = new Map();
        wallets.forEach((wallet) => {
            const currencyId = wallet?.currency;
            if (currencyId == null) return;
            const prev = byCurrency.get(currencyId) || 0;
            byCurrency.set(currencyId, prev + walletValue(wallet));
        });
        return Array.from(byCurrency.entries()).map(([currencyId, value]) => {
            const currency = currencies.find((c) => c.id === currencyId || c.id == currencyId);
            return {
                currencyId,
                currencyShortName: currency?.shortName || "",
                value
            };
        });
    }, [wallets, currencies]);

    if (!wallets || wallets.length === 0) {
        return null;
    }

    return (
        <Box sx={{marginBottom: 4}} id={MAIN_SCREEN_WALLETS_ANCHOR_ID}>
            <Typography
                variant="h6"
                color="primary"
                onClick={() => navigate("/wallets")}
                sx={{marginLeft: 4, cursor: "pointer", display: "inline-block"}}
            >
                {t("pages.transactions.walletsCard.title")}
            </Typography>
            <Paper sx={{p: 2}}>
                {wallets.map((wallet) => (
                    <Box
                        key={wallet.id}
                        sx={{display: "flex", alignItems: "center", gap: 1.5, py: 0.5}}
                    >
                        <WalletIcon image={wallet.image}/>
                        <Typography sx={{flex: 1}} noWrap>{wallet.name}</Typography>
                        <Typography sx={{fontWeight: 500}}>
                            <WalletBalance wallet={wallet}/>
                        </Typography>
                    </Box>
                ))}
                <Divider sx={{my: 1.5}}/>
                <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 1}}>
                    <Typography component="span" sx={{fontWeight: 600}}>
                        {t("pages.transactions.walletsCard.total")}:
                    </Typography>
                    {totals.map((item) => (
                        <Typography
                            key={item.currencyId}
                            component="span"
                            sx={{ml: 2, color: item.value < 0 ? "error.main" : "inherit"}}
                        >
                            {formatSigned(item.value)} {item.currencyShortName}
                        </Typography>
                    ))}
                </Box>
            </Paper>
        </Box>
    );
};

export default MainScreenWalletsCard;
